import React, { useState } from 'react';
import { TrendingUp, Calendar, DollarSign, CheckCircle } from 'lucide-react';
import { adminAPI } from '../services/api';

function TimeMachine() {
  const [selectedQuarter, setSelectedQuarter] = useState('2024-Q3');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const quarters = ['2024-Q1', '2024-Q2', '2024-Q3', '2024-Q4', '2023-Q4'];

  const runSimulation = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.runTimeMachine({
        quarter: selectedQuarter,
        campaign_ids: ['tech_q3_awareness', 'tech_q3_leads', 'retail_summer_sale']
      });
      setResults(response.data);
    } catch (error) {
      console.error('Error running time machine:', error);
      // Set demo results
      setResults({
        quarter: selectedQuarter,
        actual_decisions: {
          total_spend: 350000,
          channel_allocations: { search: 180000, social: 100000, display: 70000 }
        },
        ai_recommendations: {
          recommended_allocations: { search: 210000, social: 90000, display: 50000 },
          expected_roi: 1.85
        },
        counterfactual_outcomes: {
          actual_outcome: 525000,
          ai_outcome: 647500,
          delta: 122500
        },
        value_delta: 122500,
        value_delta_pct: 23.3,
        confidence_interval: [95000, 150000]
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 className="card-title">Time Machine - Historical Simulation</h3>
            <p className="card-subtitle">"What if we had AI in the past?" counterfactual analysis</p>
          </div>
          <button
            onClick={runSimulation}
            className="btn btn-primary"
            disabled={loading}
            data-testid="run-time-machine-btn"
          >
            <Calendar size={20} />
            {loading ? 'Running...' : 'Run Simulation'}
          </button>
        </div>
      </div>

      {/* Quarter Selector */}
      <div style={{ marginTop: '16px', marginBottom: '24px' }}>
        <label style={{ fontSize: '0.875rem', fontWeight: '600', display: 'block', marginBottom: '8px' }}>Select Quarter</label>
        <select
          value={selectedQuarter}
          onChange={(e) => setSelectedQuarter(e.target.value)}
          style={{
            padding: '10px 12px',
            border: '1px solid var(--border-light)',
            borderRadius: '8px',
            fontSize: '0.875rem',
            background: 'white',
            width: '200px'
          }}
          data-testid="quarter-selector"
        >
          {quarters.map(q => (
            <option key={q} value={q}>{q}</option>
          ))}
        </select>
      </div>

      {results ? (
        <div>
          {/* Comparison Cards */}
          <div className="grid grid-cols-3" style={{ gap: '16px', marginBottom: '24px' }}>
            <div style={{ padding: '20px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ padding: '10px', background: 'var(--secondary-500)', borderRadius: '8px', color: 'white' }}>
                  <DollarSign size={24} />
                </div>
                <div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Actual Decisions</p>
                  <p style={{ fontSize: '1.75rem', fontWeight: '700' }}>
                    ${results.counterfactual_outcomes?.actual_outcome?.toLocaleString()}
                  </p>
                </div>
              </div>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>Historical performance</p>
            </div>

            <div style={{ padding: '20px', background: 'hsl(142, 76%, 96%)', borderRadius: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ padding: '10px', background: 'var(--success-500)', borderRadius: '8px', color: 'white' }}>
                  <TrendingUp size={24} />
                </div>
                <div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>AI Recommendations</p>
                  <p style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--success-600)' }}>
                    ${results.counterfactual_outcomes?.ai_outcome?.toLocaleString()}
                  </p>
                </div>
              </div>
              <p style={{ fontSize: '0.75rem', color: 'hsl(142, 48%, 30%)' }}>With AI optimization</p>
            </div>

            <div style={{ padding: '20px', background: 'hsl(180, 65%, 97%)', borderRadius: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ padding: '10px', background: 'var(--accent-500)', borderRadius: '8px', color: 'white' }}>
                  <CheckCircle size={24} />
                </div>
                <div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Potential Value Gain</p>
                  <p style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--accent-700)' }}>
                    +${results.value_delta?.toLocaleString()}
                  </p>
                </div>
              </div>
              <p style={{ fontSize: '0.75rem', color: 'var(--accent-700)' }}>+{results.value_delta_pct}% improvement</p>
            </div>
          </div>

          {/* Detailed Breakdown */}
          <div style={{ padding: '20px', background: 'var(--bg-tertiary)', borderRadius: '12px', marginBottom: '24px' }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '16px' }}>Channel Allocation Comparison</h4>
            <div className="grid grid-cols-2" style={{ gap: '16px' }}>
              <div>
                <p style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '12px' }}>Actual Decisions ({selectedQuarter})</p>
                {results.actual_decisions?.channel_allocations && Object.entries(results.actual_decisions.channel_allocations).map(([channel, amount]) => (
                  <div key={channel} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ textTransform: 'capitalize' }}>{channel}</span>
                    <span style={{ fontWeight: '600' }}>${amount.toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <div>
                <p style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '12px', color: 'var(--success-600)' }}>AI Would Have Recommended</p>
                {results.ai_recommendations?.recommended_allocations && Object.entries(results.ai_recommendations.recommended_allocations).map(([channel, amount]) => (
                  <div key={channel} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ textTransform: 'capitalize' }}>{channel}</span>
                    <span style={{ fontWeight: '600', color: 'var(--success-600)' }}>${amount.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Confidence Interval */}
          <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
            <p style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>95% Confidence Interval on Value Gain</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <span style={{ fontSize: '1.125rem', fontWeight: '600' }}>
                ${results.confidence_interval?.[0]?.toLocaleString()}
              </span>
              <div style={{ flex: 1, height: '4px', background: 'var(--accent-500)', borderRadius: '2px' }}></div>
              <span style={{ fontSize: '1.125rem', fontWeight: '600' }}>
                ${results.confidence_interval?.[1]?.toLocaleString()}
              </span>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '8px', textAlign: 'center' }}>
              Statistical confidence in estimated value improvement
            </p>
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <Calendar size={48} className="empty-state-icon" />
          <p>Select a quarter and run simulation to see historical analysis</p>
        </div>
      )}
    </div>
  );
}

export default TimeMachine;
